package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAnIdentityProviderForFortySixMegabytesPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/AnIdentityProviderForFortySixMegabytes.md", FrontMatterElement.Builder { addScalar("title", "An identity provider for 46 MiB"); addScalar("searchTitle", "Lightweight OpenID Connect provider in Kotlin/Native — service tokens, PKCE sign-in, magic link"); addScalar("date", "2026-08-19"); addScalar("description", "What a platform actually needs from an IdP, what that costs when it ships as one native binary, and where the measurement stops being a comparison."); addScalar("library", "shildik"); }))
}

@Page("/blog/an-identity-provider-for-forty-six-megabytes")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun AnIdentityProviderForFortySixMegabytesPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Keycloak was answering three questions for us — who is this service, who is this person, and which keys verify their tokens — and asking for 640 MiB and a Postgres of its own to do it. Nothing about those three questions is heavy.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/shildik") {
            org.jetbrains.compose.web.dom.Text("shildik")
        }
        org.jetbrains.compose.web.dom.Text(" answers them from one Kotlin/Native binary against one Postgres, and it is open now. This is what it closes, and what it costs while closing it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-closes") }) {
        org.jetbrains.compose.web.dom.Text("What it closes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Service-to-service authorization.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("client_credentials")
        }
        org.jetbrains.compose.web.dom.Text(", JWKS, discovery. A service asks for a token, presents it to a neighbour, and the neighbour verifies the signature with keys it fetched itself. This is the safest part of OAuth2 — no redirects, no sessions, no cookies — and for most platforms it is also the only part they use.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Browser sign-in.")
        }
        org.jetbrains.compose.web.dom.Text(" Authorization code with PKCE, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("id_token")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("userinfo")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("end_session")
        }
        org.jetbrains.compose.web.dom.Text(", and refresh rotation with replay detection. Enough for next-auth or oauth2-proxy to treat it as an ordinary provider, because that is what they were tested against.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Sign-in methods as modules.")
        }
        org.jetbrains.compose.web.dom.Text(" Google, a magic link from an email, a password. Which ones a build carries is decided by its dependencies, not by configuration: a distribution without ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("auth-password")
        }
        org.jetbrains.compose.web.dom.Text(" cannot be talked into having passwords. In Keycloak the same magic link was a jar built against somebody else's SPI and loaded into somebody else's process; here it is a module implementing one interface.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("An internal contour where a public provider makes no sense.")
        }
        org.jetbrains.compose.web.dom.Text(" Behind an oauth2-proxy in front of your own monitoring, a Google account is the wrong question — the tenant is closed, and people are provisioned by an administrator. Same code, different ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main()")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Configuration that lives in git.")
        }
        org.jetbrains.compose.web.dom.Text(" No admin console: a CLI creates clients, roles, users and keys, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("export")
        }
        org.jetbrains.compose.web.dom.Text(" writes the lot to a file and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("apply")
        }
        org.jetbrains.compose.web.dom.Text(" puts it onto an empty instance. That is the thing a console-configured Keycloak could not do, and the reason a contour could not be raised again.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Key rotation nobody notices.")
        }
        org.jetbrains.compose.web.dom.Text(" Several signing keys live side by side, JWKS serves all of them, consumers pick by ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kid")
        }
        org.jetbrains.compose.web.dom.Text(", and a retired key stays published for 24 hours because that is how long a typical validator caches JWKS.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-costs") }) {
        org.jetbrains.compose.web.dom.Text("What it costs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three pods in production, measured over a 20-hour window by the monitoring that watches everything else here:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th()
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Resident memory")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("45–48 MiB")
                    }
                    org.jetbrains.compose.web.dom.Text(" steady, 53.7 MiB peak — on the first sample after boot")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Memory limit it lives under")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("256 MiB of cgroup, so the limit is four times the working set")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CPU")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6–7 per mille of a core, at 0.1 request/second")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Threads")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("19–23")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The peak matters more than the median: it is what a limit has to survive, and it happens at start-up rather than under load.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The comparison you want is not in that table.")
        }
        org.jetbrains.compose.web.dom.Text(" The obvious next line would be \"against N MiB on the JVM\", and our agent cannot give it honestly: for a JVM process it reports ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("heap")
        }
        org.jetbrains.compose.web.dom.Text(", and for a native one it reports ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("RSS")
        }
        org.jetbrains.compose.web.dom.Text(". A neighbouring JVM service in the same cluster shows a 21–34 MiB sawtooth against a 62 MiB max heap — which is not a number you may put next to 46 MiB of RSS, because it is not the same quantity. The JVM build of this same code exists and is kept as a rollback path; measuring both as RSS, from outside the process, is the only version of that comparison worth publishing, and I have not run it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So: 46 MiB of RSS, from monitoring, for a provider serving two live contours. Take the axis with the number or leave both.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-does-not-do") }) {
        org.jetbrains.compose.web.dom.Text("What it does not do")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Useful to know before you try it, because none of this is on the roadmap by accident:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("no SAML, no SCIM, no LDAP federation;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("no admin UI — the CLI is the interface, and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("export")
            }
            org.jetbrains.compose.web.dom.Text("/")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("apply")
            }
            org.jetbrains.compose.web.dom.Text(" is the reason;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("no user self-service: no password reset flow, no profile page, no consent screen;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("no token introspection or revocation endpoint;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("one tenant per installation in practice, though the model has tenants;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("no clustering of its own — it is stateless, and two pods behind a service is the whole story.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("assembling-one") }) {
        org.jetbrains.compose.web.dom.Text("Assembling one")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A distribution is a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main()")
        }
        org.jetbrains.compose.web.dom.Text(" that says which storage and which sign-in methods it wants:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""fun main() =
    runShildik(
        storage = { config -> sqlx4kStorageModule(config.jdbcUrl, config.dbUser, config.dbPassword) },
    ) {
        buildList {
            optional("GOOGLE_CLIENT_ID")?.let { add(GoogleAuthMethod(it, /* … */)) }
            optional("MAGIC_HANDOFF_SECRET")?.let { add(MagicLinkAuthMethod(it)) }
        }
    }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No key, no method — and the sign-in it powers simply is not offered. That is the whole configuration story: a build carries what it was given.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For the services on the other side, the same repository publishes the libraries they need — a token validator and a service-token client, both multiplatform, so a service that moves to Kotlin/Native keeps the code it already had:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""dependencies {
    implementation("io.github.youndie.shildik:oidc-auth-server:${'$'}version")
    implementation("io.github.youndie.shildik:oidc-auth-client:${'$'}version")
}
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("whether-it-is-for-you") }) {
        org.jetbrains.compose.web.dom.Text("Whether it is for you")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If you need SAML, delegated administration or a consent UI, use Keycloak — it is good at exactly the things left out here. If what you need is the three questions at the top of this page, and you would rather not run a JVM and a second database to get answers, this fits in one binary, one Postgres and 46 MiB.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/shildik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/shildik")
        }
    }
}
